/**
 * Static catalog of the 39 PNG icons under `apps/web/public/11eIcons/`,
 * grouped by the 5 banners that define the icon-palette taxonomy (spec 16 v2).
 *
 * Each entry's `id` matches the PNG filename (without extension) and is what
 * the server stores in `MarkerMetadata.iconId`.
 */
import type { IconCategory, MapMode } from '@warplan/shared';
import { ICON_LABEL_ALIASES } from './icon-label-aliases.js';

export interface IconEntry {
  /** Stable id, UPPER_SNAKE_CASE, matches `${id}.png` under `/11eIcons/`. */
  id: string;
  /** Public URL of the PNG. */
  src: string;
  /** Humanized label, used for tooltips and popover headers. */
  label: string;
}

export interface CategoryDescriptor {
  id: IconCategory;
  label: string;
  /** Icon shown on the toolbar button + fallback for new markers. */
  defaultIconId: string;
  icons: IconEntry[];
}

export type CategoryRegistry = Record<IconCategory, CategoryDescriptor>;

/** Strip the `${CATEGORY}_` prefix and title-case the remaining UPPER_SNAKE_CASE tail. */
function humanizeIconId(id: string): string {
  const idx = id.indexOf('_');
  const tail = idx === -1 ? id : id.slice(idx + 1);
  return tail
    .split('_')
    .map((part) => {
      // Preserve numeric runs like 5K, 10K, 30K, or pure digits.
      if (/^[0-9]+[A-Z]?$/.test(part)) return part;
      return part.charAt(0) + part.slice(1).toLowerCase();
    })
    .join(' ');
}

function entry(id: string): IconEntry {
  return { id, src: `/11eIcons/${id}.png`, label: ICON_LABEL_ALIASES[id] ?? humanizeIconId(id) };
}

function sorted(icons: IconEntry[]): IconEntry[] {
  return [...icons].sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
}

const COMBAT: IconEntry[] = sorted([
  'COMBAT_ATTACK',
  'COMBAT_OBJECTIVE',
  'COMBAT_PARTISAN',
  'COMBAT_PATROL',
  'COMBAT_SUPPORT',
].map(entry));

const BUILD: IconEntry[] = sorted(['BUILD_BASE'].map(entry));

const FACILITY: IconEntry[] = sorted([
  'FACILITY_AMMO',
  'FACILITY_AMMOSTORAGE',
  'FACILITY_ASSMAT_1',
  'FACILITY_ASSMAT_2',
  'FACILITY_ASSMAT_3',
  'FACILITY_ASSMAT_4',
  'FACILITY_ASSMAT_5',
  'FACILITY_COAL',
  'FACILITY_COMPONENT',
  'FACILITY_CONCRETE',
  'FACILITY_DRYDOCK',
  'FACILITY_IKF',
  'FACILITY_LARGEPAD',
  'FACILITY_MATERIAL',
  'FACILITY_MSUP',
  'FACILITY_OIL',
  'FACILITY_PAD',
  'FACILITY_PLANE',
  'FACILITY_RAREMAT',
  'FACILITY_SALVAGE',
  'FACILITY_SULFUR',
  'FACILITY_TRAINYARD',
].map(entry));

const LOGISTIC: IconEntry[] = sorted([
  'LOGISTIC_AIRWAY',
  'LOGISTIC_HUB',
  'LOGISTIC_MAINTENANCETUNNEL',
  'LOGISTIC_MSUP_5K',
  'LOGISTIC_MSUP_10K',
  'LOGISTIC_MSUP_30K',
  'LOGISTIC_SEAPORT',
  'LOGISTIC_STORAGEDEPOT',
].map(entry));

const NAVY: IconEntry[] = sorted([
  'NAVY_BATTLESHIP',
  'NAVY_BLUEFIN',
  'NAVY_BOWHEAD',
  'NAVY_FRIGATE',
  'NAVY_LONGHOOK',
  'NAVY_MERCY',
  'NAVY_SUBMARINE',
].map(entry));

// ---------------------------------------------------------------------------
// Operation-mode catalog
//
// Icon ids follow the on-disk PNG filenames under `apps/web/public/11eIcons/`:
//   `OPERATION_<CATEGORY>_<TYPE>.png`
// where <CATEGORY> matches the operation sub-category (INFO / ATTACK / DEFEND
// / ARTY / BUILD / NAVY / UNIT / VEHICLE). Display order is INFO, ATTACK,
// DEFEND, then the remaining categories alphabetically.
// ---------------------------------------------------------------------------

const OP_INFO: IconEntry[] = sorted([
  'OPERATION_INFO_AIRDEPOT',
  'OPERATION_INFO_AMMODEPOT',
  'OPERATION_INFO_RALLYPOINT',
  'OPERATION_INFO_SEAPORT',
  'OPERATION_INFO_STORAGEDEPOT',
].map(entry));

const OP_ATTACK: IconEntry[] = sorted([
  'OPERATION_ATTACK_MAIN_OBJECTIVE',
  'OPERATION_ATTACK_OBJECTIVE_1',
  'OPERATION_ATTACK_OBJECTIVE_2',
  'OPERATION_ATTACK_OBJECTIVE_3',
  'OPERATION_ATTACK_OBJECTIVE_A',
  'OPERATION_ATTACK_OBJECTIVE_B',
  'OPERATION_ATTACK_OBJECTIVE_C',
].map(entry));

const OP_DEFEND: IconEntry[] = sorted([
  'OPERATION_DEFEND_MAIN_OBJECTIVE',
  'OPERATION_DEFEND_OBJECTIVE_1',
  'OPERATION_DEFEND_OBJECTIVE_2',
  'OPERATION_DEFEND_OBJECTIVE_3',
].map(entry));

const OP_ARTY: IconEntry[] = sorted([
  'OPERATION_ARTY_120',
  'OPERATION_ARTY_150',
  'OPERATION_ARTY_FIELDROCKET',
  'OPERATION_ARTY_HALFTRACKROCKET',
  'OPERATION_ARTY_RSC',
  'OPERATION_ARTY_SPG',
].map(entry));

const OP_BUILD: IconEntry[] = sorted([
  'OPERATION_BUILD_PUSH',
  'OPERATION_BUILD_TAP',
].map(entry));

const OP_NAVY: IconEntry[] = sorted([
  'OPERATION_NAVY_BATTLESHIP',
  'OPERATION_NAVY_BLUEFIN',
  'OPERATION_NAVY_BOWHEAD',
  'OPERATION_NAVY_FRIGATE',
  'OPERATION_NAVY_LONGHOOK',
  'OPERATION_NAVY_MERCY',
  'OPERATION_NAVY_SUBMARINE',
].map(entry));

const OP_UNIT: IconEntry[] = sorted([
  'OPERATION_UNIT_INFANTRY',
  'OPERATION_UNIT_LOGISTIC',
].map(entry));

const OP_VEHICLE: IconEntry[] = sorted([
  'OPERATION_VEHICLE_FIELDCANNON',
  'OPERATION_VEHICLE_HALFTRACK',
  'OPERATION_VEHICLE_HALFTRACKAT',
  'OPERATION_VEHICLE_RECON',
  'OPERATION_VEHICLE_SPAA',
  'OPERATION_VEHICLE_TANK_HEAVY',
  'OPERATION_VEHICLE_TANK_LIGHT',
  'OPERATION_VEHICLE_TANK_MEDIUM',
  'OPERATION_VEHICLE_TANK_SUPERHEAVY',
].map(entry));

export const ICON_CATALOG: CategoryRegistry = {
  combat: {
    id: 'combat',
    label: 'Combat',
    defaultIconId: 'COMBAT_OBJECTIVE',
    icons: COMBAT,
  },
  build: {
    id: 'build',
    label: 'Build',
    defaultIconId: 'BUILD_BASE',
    icons: BUILD,
  },
  facility: {
    id: 'facility',
    label: 'Facility',
    defaultIconId: 'FACILITY_PAD',
    icons: FACILITY,
  },
  logistic: {
    id: 'logistic',
    label: 'Logistic',
    defaultIconId: 'LOGISTIC_HUB',
    icons: LOGISTIC,
  },
  navy: {
    id: 'navy',
    label: 'Navy',
    defaultIconId: 'NAVY_FRIGATE',
    icons: NAVY,
  },
  op_info: {
    id: 'op_info',
    label: 'Info',
    defaultIconId: 'OPERATION_INFO_RALLYPOINT',
    icons: OP_INFO,
  },
  op_attack: {
    id: 'op_attack',
    label: 'Attack',
    defaultIconId: 'OPERATION_ATTACK_MAIN_OBJECTIVE',
    icons: OP_ATTACK,
  },
  op_defend: {
    id: 'op_defend',
    label: 'Defend',
    defaultIconId: 'OPERATION_DEFEND_MAIN_OBJECTIVE',
    icons: OP_DEFEND,
  },
  op_arty: {
    id: 'op_arty',
    label: 'Artillery',
    defaultIconId: 'OPERATION_ARTY_120',
    icons: OP_ARTY,
  },
  op_build: {
    id: 'op_build',
    label: 'Build',
    defaultIconId: 'OPERATION_BUILD_PUSH',
    icons: OP_BUILD,
  },
  op_navy: {
    id: 'op_navy',
    label: 'Navy',
    defaultIconId: 'OPERATION_NAVY_FRIGATE',
    icons: OP_NAVY,
  },
  op_unit: {
    id: 'op_unit',
    label: 'Unit',
    defaultIconId: 'OPERATION_UNIT_INFANTRY',
    icons: OP_UNIT,
  },
  op_vehicle: {
    id: 'op_vehicle',
    label: 'Vehicle',
    defaultIconId: 'OPERATION_VEHICLE_TANK_MEDIUM',
    icons: OP_VEHICLE,
  },
};

export const ORDERED_CATEGORIES: IconCategory[] = [
  'combat',
  'build',
  'facility',
  'logistic',
  'navy',
];

export const ORDERED_CATEGORIES_OPERATION: IconCategory[] = [
  'op_info',
  'op_attack',
  'op_defend',
  'op_arty',
  'op_build',
  'op_navy',
  'op_unit',
  'op_vehicle',
];

export const ORDERED_CATEGORIES_BY_MODE: Record<MapMode, IconCategory[]> = {
  planner: ORDERED_CATEGORIES,
  operation: ORDERED_CATEGORIES_OPERATION,
};

export function getOrderedCategoriesForMode(mode: MapMode): IconCategory[] {
  return ORDERED_CATEGORIES_BY_MODE[mode];
}

/** Resolve `(kind, iconId)` to an `IconEntry`. Falls back to category default. */
export function getIconEntry(kind: IconCategory, iconId: string): IconEntry {
  const cat = ICON_CATALOG[kind];
  const found = cat.icons.find((i) => i.id === iconId);
  if (found) return found;
  const fallback = cat.icons.find((i) => i.id === cat.defaultIconId);
  return fallback ?? cat.icons[0]!;
}

export { humanizeIconId };
